import {Component, OnDestroy, OnInit} from "@angular/core";
import {noop, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {NotificationService, Notification} from "@pb-labs/notification";
import {validateUserEmail} from "@app/modules/browse/components/settings/utils";
import {UserInfo, UserSettingsService} from "@app/modules/browse/components/settings/settings.service";
import {PublicKeyUploadModalService} from "@app/modules/browse/components/public-key-upload-modal/public-key-upload-modal.service";

@Component({
  selector: "app-settings",
  templateUrl: "./settings.component.html",
  styleUrls: ["./settings.component.scss"],
})
export class SettingsComponent implements OnInit, OnDestroy {
  userInfo: UserInfo = null;
  emailValidationMessage: string = null;

  private _destroy$: Subject<void> = new Subject<void>();

  constructor(
    private userSettingsService: UserSettingsService,
    private notificationService: NotificationService,
    private publicKeyUploadModalService: PublicKeyUploadModalService
  ) {}

  ngOnInit(): void {
    this.userSettingsService.initialize();
    this.publicKeyUploadModalService.initialize();

    this.userSettingsService.userInfo$.pipe(takeUntil(this._destroy$)).subscribe((userInfo) => {
      this.userInfo = userInfo;
    });
  }

  ngOnDestroy(): void {
    this.userSettingsService.destroy();
    this.publicKeyUploadModalService.destroy();

    this._destroy$.next();
    this._destroy$.complete();
  }

  onUserInfoUpdate(userInfo: UserInfo): void {
    this.userSettingsService.updateUserInfo(userInfo);
  }

  onUserEmailChange(value: string): void {
    this.onUserInfoUpdate({...this.userInfo, email: value});
    this.emailValidationMessage = value ? validateUserEmail(value) : null;
  }

  onUserEmailVerificationCodeChange(value: string): void {
    this.onUserInfoUpdate({...this.userInfo, emailVerificationCode: value});
  }

  onUserPubKeyChange(value: string): void {
    this.onUserInfoUpdate({...this.userInfo, sshKey: value});
  }

  isUserInfoLoading(): boolean {
    return this.userSettingsService.userInfoLoading;
  }

  isUserInfoInputDisabled(): boolean {
    return this.userSettingsService.userInfoUpdateLoading;
  }

  onSaveUserInfoButtonClick(): void {
    this.userSettingsService.saveUserInfo().subscribe({
      next: noop,
      error: () => {
        this.notificationService.showError(
          new Notification({
            message: "User email update failed. Please try again.",
            timeout: 5000,
          })
        );
      },
    });
  }

  isSaveUserInfoButtonBusy(): boolean {
    return this.userSettingsService.userInfoUpdateLoading;
  }

  isSaveUserInfoButtonDisabled(): boolean {
    return (
      !!this.emailValidationMessage ||
      !this.userInfo?.email ||
      !this.userSettingsService.isUserInfoChanged() ||
      this.userSettingsService.userEmailVerifying
    );
  }

  canShowVerifyEmail(): boolean {
    return !!this.userInfo?.nonVerifiedEmail;
  }

  isVerifyEmailButtonDisabled(): boolean {
    return this.userSettingsService.userInfoUpdateLoading || !this.userInfo?.emailVerificationCode?.trim();
  }

  isVerifyEmailButtonBusy(): boolean {
    return this.userSettingsService.userEmailVerifying;
  }

  onVerifyEmailButtonClick(): void {
    this.userSettingsService.verifyEmail().subscribe({
      next: () => {
        this.notificationService.showSuccess(
          new Notification({
            message: "New Email was successfully saved.",
            timeout: 5000,
          })
        );
      },
      error: () => {
        this.notificationService.showError(
          new Notification({
            message: "The entered validation code is incorrect.",
            timeout: 5000,
          })
        );
      },
    });
  }

  onOpenButtonClick(): void {
    this.publicKeyUploadModalService
      .startDialog$()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (value: string) => {
          this.onUserPubKeyChange(value);
        },
        error: noop,
      });
  }
}
